namespace distill.datasets.views.settings {
    class InitArgs {
        existingColumns: IColumn[] = [];
        datasetId: string = appn.emptyString;
        columnTypes: { value: string; text: string }[] = [];
        isCruxSource: boolean = false;
        isCreateMode: boolean = false;
        originalPrimaryKeyIds: string[] = [];
        columnIdsInUse: string[] = [];
        orphanedColumnIds: string[] = [];
        availableNewColumns: IAvailableNewColumns | null = null;
    }

    interface IAvailableNewColumns {
        autoColumns: IColumn[];
        singularFields: IColumn[];
        repeatFields: IColumn[];
        existingColumnOrders: { displayName: string; displayOrder: number }[];
    }

    let _initArgs: InitArgs;
    let _columnBuilder: ColumnBuilder;
    let _originalColumns: IColumn[] = [];
    let $mainForm: JQuery = $();
    let _availableNewColumns: IAvailableNewColumns | null = null;

    export function init(args: InitArgs): void {
        _initArgs = args;
        _originalColumns = JSON.parse(JSON.stringify(_initArgs.existingColumns || []));

        setColumnTypes(_initArgs.columnTypes);

        $(function() {
            $mainForm = $('form[action*="Datasets/Settings"]:first');
            bindEvents();
        });
    }

    function bindEvents(): void {
        if (_initArgs.orphanedColumnIds?.length > 0) {
            _initArgs.existingColumns.forEach(col => {
                if (_initArgs.orphanedColumnIds.includes(col.id))
                    col.isOrphaned = true;
            });
        }

        const config: IBuilderConfig = {
            isCruxSource: _initArgs.isCruxSource,
            isCreateMode: _initArgs.isCreateMode,
            containerId: 'schemaColumnsList',
            showPk: true,
            showType: true,
            showRequired: true,
            showUnique: true,
            allowTypeChanges: !_initArgs.isCruxSource,
            readonlyRequiredUnique: _initArgs.isCruxSource,
            originalPrimaryKeyIds: _initArgs.originalPrimaryKeyIds,
            columnIdsInUse: _initArgs.columnIdsInUse,
            orphanedColumnIds: _initArgs.orphanedColumnIds,
            onChanged: () => {
                ColumnBuilder.refreshIndexes([{ builder: _columnBuilder }]);
            }
        };

        _columnBuilder = new ColumnBuilder(_initArgs.existingColumns, config);

        $('#addColumnBtn').off('click').on('click', function() {
            _columnBuilder.addColumn();
        });

        $('#includeCruxColumnsBtn').off('click').on('click', function() {
            openIncludeCruxColumnsModal();
        });

        $('#confirmIncludeCruxColumns').off('click').on('click', function() {
            confirmIncludeCruxColumns();
        });

        $(document).off('click', '.delete-column-btn').on('click', '.delete-column-btn', function(e) {
            e.preventDefault();
            const columnId = $(e.currentTarget).data('column-id');
            _columnBuilder.deleteColumn(columnId);
        });

        const $trashBtn = $('nav .navbar-nav-right button[value*="Trash"]');
        if ($trashBtn.length > 0)
            trash.addTrashMessage($trashBtn, '#Name', undefined, undefined, false);

        $('#saveButton').off('click').on('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            ColumnBuilder.refreshIndexes([{ builder: _columnBuilder }]);

            if (!$mainForm.valid()) {
                const $firstError = $('.field-validation-error:visible').first();
                if ($firstError.length)
                    $firstError[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
                
                return false;
            }

            const changeSummary = getDetailedChangeSummary();

            if (changeSummary.hasChanges) {
                displaySchemaChangeModal(changeSummary);
                return false;
            } else
                ($mainForm[0] as HTMLFormElement).submit();
        });

        $('#confirmSchemaChanges').off('click').on('click', function() {
            $mainForm.find('input[name="hasColumnChanges"]').remove();
            $('<input type="hidden" name="hasColumnChanges" value="true">').appendTo($mainForm);
            $('#schemaChangeModal').modal('hide');
            ($mainForm[0] as HTMLFormElement).submit();
        });
    }

    function getCurrentColumnsFromDOM(): IColumn[] {
        const columns: IColumn[] = [];

        $('#schemaColumnsList tbody tr').each(function(index: number) {
            const $row = $(this);
            if ($row.hasClass('column-row-excluded'))
                return;

            const columnId = $row.data('column-id')?.toString() || '';
            if (!columnId)
                return;

            const designation = ($row.data('designation') as string) || null;
            const isNew = $row.data('is-new') === true;

            const displayName = $row.find('.column-name').val()?.toString() || '';
            const type = $row.find('.column-type').val()?.toString() || 'Text';
            const required = $row.find('.column-required').is(':checked');
            const unique = $row.find('.column-unique').is(':checked');
            const defaultValue = $row.find('.column-default').val()?.toString() || '';
            const isPrimaryKey = $row.find('.column-pk-hidden').val() === 'true';

            columns.push({
                id: columnId,
                displayName: displayName,
                type: type,
                required: required,
                unique: unique,
                defaultValue: defaultValue,
                designation: designation,
                isPrimaryKey: isPrimaryKey,
                displayOrder: index + 1,
                isNew: isNew
            });
        });

        return columns;
    }

    interface IColumnChange {
        columnName: string;
        changeType: 'added' | 'removed' | 'modified';
        details: string[];
    }

    interface IDetailedChangeSummary {
        hasChanges: boolean;
        changes: IColumnChange[];
        hasReorder: boolean;
    }

    function getDetailedChangeSummary(): IDetailedChangeSummary {
        const currentColumns = getCurrentColumnsFromDOM();
        const changes: IColumnChange[] = [];

        const originalFiltered = _originalColumns.filter(c => c.designation !== 'RowLastUpdated');
        const currentFiltered = currentColumns.filter(c => c.designation !== 'RowLastUpdated');

        const originalMap = new Map<string, IColumn>(originalFiltered.map(c => [c.id, c]));
        const currentMap = new Map<string, IColumn>(currentFiltered.map(c => [c.id, c]));

        const addedIds = Array.from(currentMap.keys()).filter(id => !originalMap.has(id));
        addedIds.forEach(id => {
            const col = currentMap.get(id);
            if (col) {
                const details = [T('Type') + ': ' + getTypeLabel(col.type)];
                if (col.isPrimaryKey)
                    details.push(T('Primary Key') + ': ' + T('Yes'));

                changes.push({
                    columnName: col.displayName || T('Unnamed Column'),
                    changeType: 'added',
                    details: details
                });
            }
        });

        const removedIds = Array.from(originalMap.keys()).filter(id => !currentMap.has(id));
        removedIds.forEach(id => {
            const col = originalMap.get(id);
            // Only flag columns as "Removed" if they are not AlwaysIncluded and not Primary Keys
            if (col && !col.alwaysIncluded && !col.isPrimaryKey) {
                changes.push({
                    columnName: col.displayName,
                    changeType: 'removed',
                    details: []
                });
            }
        });

        currentMap.forEach((currentCol, id) => {
            const originalCol = originalMap.get(id);
            if (!originalCol)
                return;

            const details: string[] = [];

            if (originalCol.displayName !== currentCol.displayName)
                details.push(T('Name') + ': "' + originalCol.displayName + '" → "' + currentCol.displayName + '"');

            if (originalCol.type !== currentCol.type) {
                const oldType = getTypeLabel(originalCol.type);
                const newType = getTypeLabel(currentCol.type);
                details.push(T('Type') + ': ' + oldType + ' → ' + newType);
            }

            if (originalCol.required !== currentCol.required)
                details.push(T('Required') + ': ' + (currentCol.required ? T('Yes') : T('No')));

            if (originalCol.unique !== currentCol.unique)
                details.push(T('Unique') + ': ' + (currentCol.unique ? T('Yes') : T('No')));

            if (originalCol.isPrimaryKey !== currentCol.isPrimaryKey)
                details.push(T('Primary Key') + ': ' + (currentCol.isPrimaryKey ? T('Yes') : T('No')));

            const originalDefault = originalCol.defaultValue || '';
            const currentDefault = currentCol.defaultValue || '';
            if (originalDefault !== currentDefault) {
                const oldVal = originalDefault || T('(none)');
                const newVal = currentDefault || T('(none)');
                details.push(T('Default Value') + ': "' + oldVal + '" → "' + newVal + '"');
            }

            if (details.length > 0) {
                changes.push({
                    columnName: currentCol.displayName,
                    changeType: 'modified',
                    details: details
                });
            }
        });

        // Use the filtered lists here so we don't accidentally trigger a re-order prompt
        // if RowLastUpdated shifts internally.
        const hasReorder = function checkOrder(original: IColumn[], current: IColumn[]): boolean {
            if (original.length !== current.length)
                return false;

            for (let i = 0; i < original.length; i++) {
                if (original[i].id !== current[i].id)
                    return true;
            }

            return false;
        }(originalFiltered, currentFiltered);

        return {
            hasChanges: changes.length > 0 || hasReorder,
            changes: changes,
            hasReorder: hasReorder && changes.length === 0
        };
    }

    function displaySchemaChangeModal(changeSummary: IDetailedChangeSummary): void {
        let html = '';
        if (changeSummary.changes.length > 0) {
            const added = changeSummary.changes.filter(c => c.changeType === 'added');
            const removed = changeSummary.changes.filter(c => c.changeType === 'removed');
            const modified = changeSummary.changes.filter(c => c.changeType === 'modified');

            if (added.length > 0) {
                html += '<div class="mb-3"><strong class="text-success"><i class="fa fa-plus-circle"></i> ' + T('Added Columns') + ' (' + added.length + '):</strong><ul class="mt-2">';
                added.forEach(change => {
                    html += '<li><strong>' + change.columnName + '</strong>';
                    if (change.details.length > 0) {
                        html += '<ul class="text-muted">';
                        change.details.forEach(detail => {
                            html += '<li>' + detail + '</li>';
                        });
                        html += '</ul>';
                    }
                    html += '</li>';
                });
                html += '</ul></div>';
            }

            if (removed.length > 0) {
                html += '<div class="mb-3"><strong class="text-danger"><i class="fa fa-minus-circle"></i> ' + T('Removed Columns') + ' (' + removed.length + '):</strong><ul class="mt-2">';
                removed.forEach(change => {
                    html += '<li><strong>' + change.columnName + '</strong></li>';
                });
                html += '</ul></div>';
            }

            if (modified.length > 0) {
                html += '<div class="mb-3"><strong class="text-primary"><i class="fa fa-edit"></i> ' + T('Modified Columns') + ' (' + modified.length + '):</strong><ul class="mt-2">';
                modified.forEach(change => {
                    html += '<li><strong>' + change.columnName + '</strong>';
                    if (change.details.length > 0) {
                        html += '<ul class="text-muted">';
                        change.details.forEach(detail => {
                            html += '<li>' + detail + '</li>';
                        });
                        html += '</ul>';
                    }
                    html += '</li>';
                });
                html += '</ul></div>';
            }
        }

        if (changeSummary.hasReorder)
            html += '<div class="mb-3"><strong class="text-info"><i class="fa fa-arrows-v"></i> ' + T('Column Order Changed') + '</strong></div>';

        $('#schemaChangeSummary').html(html);
        $('#schemaChangeModal').modal('show');
    }

    function openIncludeCruxColumnsModal(): void {
        const data = _initArgs.availableNewColumns;
        if (!data)
            return;

        _availableNewColumns = data;

        if (data.existingColumnOrders) {
            data.existingColumnOrders.forEach(eco => {
                const existingCol = _columnBuilder.columns.find(c =>
                    c.displayName.toLowerCase() === eco.displayName.toLowerCase()
                );
                if (existingCol)
                    existingCol.displayOrder = eco.displayOrder;
            });
        }

        populateIncludeCruxColumnSelects(data);
        $('#includeCruxColumnsModal').modal('show');
    }

    function populateIncludeCruxColumnSelects(data: IAvailableNewColumns): void {
        const $autoSelect = $('#includeCruxColumnSelectAuto');
        const $singularSelect = $('#includeCruxColumnSelectSingular');
        const $repeatSelect = $('#includeCruxColumnSelectRepeat');

        if ($autoSelect.length)
            ($autoSelect as any).bootstrapDualListbox('refresh');
        if ($singularSelect.length)
            ($singularSelect as any).bootstrapDualListbox('refresh');
        if ($repeatSelect.length)
            ($repeatSelect as any).bootstrapDualListbox('refresh');

        $('#includeCruxColumnsModal .multiselect').off('change').on('change', updateConfirmButton);
        updateConfirmButton();
    }

    function updateConfirmButton(): void {
        const selectedIds = new Set(getSelectedFieldIds());
        const stagedIds = stagedDialogColumnIds();

        const hasChanges = stagedIds.some(id => !selectedIds.has(id))
            || [...selectedIds].some(id => !stagedIds.includes(id));

        $('#confirmIncludeCruxColumns').prop('disabled', !hasChanges);
    }

    function getSelectedFieldIds(): string[] {
        const auto = ($('#includeCruxColumnSelectAuto').val() as string[]) || [];
        const singular = ($('#includeCruxColumnSelectSingular').val() as string[]) || [];
        const repeat = ($('#includeCruxColumnSelectRepeat').val() as string[]) || [];
        return [...auto, ...singular, ...repeat];
    }

    function confirmIncludeCruxColumns(): void {
        const selectedIds = new Set(getSelectedFieldIds());
        const dialogFields = allAvailableNewFields();
        const stagedIds = new Set(stagedDialogColumnIds());

        const toAdd = dialogFields.filter(f => selectedIds.has(f.id) && !stagedIds.has(f.id));
        const toRemove = [...stagedIds].filter(id => !selectedIds.has(id));

        toRemove.forEach(id => _columnBuilder.deleteColumn(id));

        if (toAdd.length > 0) {
            _columnBuilder.addIncludeCruxColumns(toAdd.map(field => ({
                id: field.id,
                fieldName: field.fieldName || field.displayName,
                displayName: field.displayName,
                type: field.type || 'Text',
                required: field.required || false,
                unique: field.unique || false,
                defaultValue: field.defaultValue || '',
                isPrimaryKey: field.isPrimaryKey || false,
                displayOrder: field.displayOrder || 0,
                designation: field.designation,
                columnCategory: field.columnCategory,
                alwaysIncluded: field.alwaysIncluded !== undefined ? field.alwaysIncluded : !!field.designation,
                cruxRepeatName: field.cruxRepeatName || '',
                isNew: true
            })));
        }

        $('#includeCruxColumnsModal').modal('hide');

        if (toAdd.length > 0)
            appn.toast.notify(appn.toast.Type.success,
                T('{0} field(s) added. Review and click Save to apply changes.', toAdd.length));
    }

    function allAvailableNewFields(): IColumn[] {
        return [
            ...(_availableNewColumns?.autoColumns || []),
            ...(_availableNewColumns?.singularFields || []),
            ...(_availableNewColumns?.repeatFields || [])
        ];
    }

    function stagedDialogColumnIds(): string[] {
        const dialogIds = new Set(allAvailableNewFields().map(f => f.id));
        return _columnBuilder.columns.filter(c => c.isNew && dialogIds.has(c.id)).map(c => c.id);
    }
}
